import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { convertToHTML } from 'draft-convert';
import { slugify } from 'transliteration';
import { toast } from 'react-toastify';

import ThirdpartyUsersService from 'src/modules/3rd-party-management/apis/common/ThirdpartyUsersService';
import DateUtils from 'src/services/utils/DateUtils';
import {
    getGeneratedFormSections,
    getInitialFormValues,
    getNestedValue,
    mapAiResponseToFormValues,
    setNestedValue,
} from '../constants/fieldDefinitions';

const useCarrierAiForm = ({
    t,
    user,
    organization,
    countriesList,
}) => {
    const [formValues, setFormValues] = useState(getInitialFormValues());
    const [aiFieldsVisible, setAiFieldsVisible] = useState(false);

    const updateField = useCallback((key, value) => {
        setFormValues((currentValues) => {
            if (key.includes('.')) {
                return setNestedValue(currentValues, key, value);
            }

            if (key === 'type' && value !== currentValues.type) {
                setAiFieldsVisible(false);
                return {
                    ...getInitialFormValues(value),
                    type: value,
                };
            }

            return {
                ...currentValues,
                [key]: value,
            };
        });
    }, []);

    const applyAiData = useCallback((aiData) => {
        setAiFieldsVisible(true);

        if (!aiData) {
            return;
        }

        setFormValues((currentValues) => mapAiResponseToFormValues(
            currentValues.type,
            currentValues,
            aiData,
            countriesList
        ));
    }, [countriesList]);

    const createMutation = useMutation({
        mutationFn: async (payload) => {
            const service = ThirdpartyUsersService.getInstance();
            return await service.create(payload);
        },
    });

    const checkExistenceMutation = useMutation({
        mutationFn: async (payload) => {
            const service = ThirdpartyUsersService.getInstance();
            return await service.checkThirdpartyExistence(payload);
        },
    });

    const buildCreatePayload = useCallback(() => {
        const dateUtils = new DateUtils();
        const languages = organization.languages.map((language) => ({
            name: language.name,
            direction: language.direction,
            language: language.language?.id,
            isDefault: language.is_default,
            module: language.module?.id,
        }));

        const supplierLocations = [];

        if (formValues.country?.value) {
            supplierLocations.push({
                type: 'office',
                country: formValues.country.value,
                city: formValues.location?.city,
                postalCode: formValues.location?.postalCode,
                address: formValues.location?.address,
            });
        }

        const descriptionContent = formValues.description?.getCurrentContent?.();
        const description = descriptionContent?.hasText?.()
            ? convertToHTML(descriptionContent)
            : '';

        const payload = {
            type: formValues.type,
            subType: formValues.subType,
            customSubType: formValues.subType === 'other' ? formValues.customSubType : undefined,
            token: localStorage.getItem('LOCAL_STORAGE_KEY_ACCESS_TOKEN'),
            name: formValues.name,
            shortName: formValues.shortName || undefined,
            domainName: slugify(formValues.name),
            languages,
            products: [],
            supplierLocations,
            industries: [],
            companySize: formValues.type === 'company' ? formValues.companySize : undefined,
            vatNumber: formValues.vatNumber || undefined,
            websiteUrl: formValues.websiteUrl || undefined,
            brands: formValues.brands || undefined,
            description,
            isPartner: 0,
            partnerAnalysts: [String(user?.id)],
            partner: Number(organization?.customerId),
            hasDashboard: 0,
            subscriptionType: '',
            contactPersons: [],
            legalEntities: [],
            countryCode: formValues.country?.code,
        };

        if (formValues.type === 'individual') {
            payload.individualDateOfBirth = formValues.individualDateOfBirth
                ? dateUtils.convertDateToDate(formValues.individualDateOfBirth, 'YYYY-MM-DD')
                : undefined;
            payload.individualBirthPlaceCountry = formValues.individualBirthPlaceCountryId?.value;
            payload.individualTaxId = formValues.individualTaxId || undefined;
            payload.individualNationalIdNumber = formValues.individualNationalIdNumber || undefined;
        }

        return payload;
    }, [formValues, organization, user]);

    const validateForm = useCallback(() => {
        if (!aiFieldsVisible) {
            return false;
        }

        const sections = getGeneratedFormSections(formValues.type, formValues);
        const invalidFields = [];

        sections.forEach((section) => {
            section.fields.forEach((field) => {
                if (!field.required) {
                    return;
                }

                const value = getNestedValue(formValues, field.key);

                if (field.type === 'country') {
                    if (!value?.value) {
                        invalidFields.push(field.labelKey);
                    }
                    return;
                }

                if (!value) {
                    invalidFields.push(field.labelKey);
                }
            });
        });

        if (invalidFields.length) {
            toast(t('There was a validation error. Please check your input and try again.'), {
                type: 'error',
            });
            return false;
        }

        return true;
    }, [aiFieldsVisible, formValues, t]);

    const submitForm = useCallback(async ({ onSuccess, onError }) => {
        if (!validateForm()) {
            return;
        }

        const payload = buildCreatePayload();

        checkExistenceMutation.mutate({
            name: payload.name,
            websiteUrl: payload.websiteUrl,
            vatNumber: payload.vatNumber,
        }, {
            onSuccess: () => {
                createMutation.mutate(payload, {
                    onSuccess,
                    onError,
                });
            },
            onError: (error) => {
                if (error.response?.data?.error === 'invalid_vatNumber') {
                    toast(t('A company with this VAT ID already exists.'), {
                        type: 'error',
                    });
                } else {
                    toast(t('A company with this name already exists.'), {
                        type: 'error',
                    });
                }

                onError?.(error);
            },
        });
    }, [
        buildCreatePayload,
        checkExistenceMutation,
        createMutation,
        t,
        validateForm,
    ]);

    const isSubmitting = useMemo(() => {
        return createMutation.isLoading || checkExistenceMutation.isLoading;
    }, [checkExistenceMutation.isLoading, createMutation.isLoading]);

    return {
        formValues,
        aiFieldsVisible,
        updateField,
        applyAiData,
        submitForm,
        isSubmitting,
        validateForm,
        setAiFieldsVisible,
    };
};

export default useCarrierAiForm;
